import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID; // Services ID, e.g. app.shuwa.web
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI; // must be an HTTPS page that echoes the response back here

// One promise per script, so a remount (StrictMode, or the effect re-running)
// waits for the in-flight load instead of resolving before the SDK exists.
const scriptPromises = {};

function loadScript(src, id) {
  if (!scriptPromises[id]) {
    scriptPromises[id] = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        delete scriptPromises[id];
        script.remove();
        reject(new Error(`Failed to load ${src}`));
      };
      document.body.appendChild(script);
    });
  }
  return scriptPromises[id];
}

/**
 * Renders "Continue with Google" / "Continue with Apple" buttons using each
 * provider's own web SDK, and exchanges the resulting identity token with the
 * SHUWA API for a session token. No-ops (renders nothing) for a provider
 * whose client ID env var isn't set, so this is safe to drop in anywhere.
 */
export default function SocialAuthButtons({ onError, onSuccess, className = "" }) {
  const { loginWithGoogle, loginWithApple } = useAuth();
  const googleButtonRef = useRef(null);
  const [appleReady, setAppleReady] = useState(false);
  // Latest callbacks, read at sign-in time so the SDK setup effects don't
  // re-run (and re-initialize the SDK) every time AuthProvider re-renders.
  const handlersRef = useRef({});
  handlersRef.current = { loginWithGoogle, loginWithApple, onError, onSuccess };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    loadScript("https://accounts.google.com/gsi/client", "google-identity-services")
      .then(() => {
        if (cancelled || !window.google || !googleButtonRef.current) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            const { loginWithGoogle, onError, onSuccess } = handlersRef.current;
            try {
              const user = await loginWithGoogle(response.credential);
              onSuccess?.(user);
            } catch (e) {
              onError?.(e?.response?.data?.message || "Google sign-in failed.");
            }
          },
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          width: Math.min(320, googleButtonRef.current.offsetWidth || 320),
          text: "continue_with",
        });
      })
      .catch(() => handlersRef.current.onError?.("Could not load Google sign-in."));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!APPLE_CLIENT_ID) return;
    let cancelled = false;

    loadScript(
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
      "apple-identity-services"
    )
      .then(() => {
        if (cancelled || !window.AppleID) return;
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: APPLE_REDIRECT_URI || window.location.href,
          usePopup: true,
        });
        setAppleReady(true);
      })
      .catch(() => handlersRef.current.onError?.("Could not load Apple sign-in."));

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAppleClick = async () => {
    try {
      const res = await window.AppleID.auth.signIn();
      const fullName = res?.user?.name
        ? [res.user.name.firstName, res.user.name.lastName].filter(Boolean).join(" ")
        : undefined;
      const user = await loginWithApple(res.authorization.id_token, fullName);
      onSuccess?.(user);
    } catch (e) {
      if (e?.error === "popup_closed_by_user") return;
      onError?.(e?.response?.data?.message || "Apple sign-in failed.");
    }
  };

  if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {GOOGLE_CLIENT_ID && <div ref={googleButtonRef} className="flex justify-center" />}
      {APPLE_CLIENT_ID && (
        <button
          type="button"
          onClick={handleAppleClick}
          disabled={!appleReady}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-900 bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
        >
          <AppleLogo />
          Continue with Apple
        </button>
      )}
    </div>
  );
}

function AppleLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}
